import {
  createMemoryHistory,
  createRouter,
  createWebHistory,
  type NavigationGuardNext,
  type RouteLocationNormalizedGeneric
} from 'vue-router'
import NProgress from 'nprogress'
import { auth } from '@/stores/auth'
import { sendPageVisit } from '@/services/tracking'
import { context } from '@/services'
import { getOrCreateSession } from '@/services/tracking-sessions'
import { useGlobalStore } from '@/stores/global'
import { searchCivilRegistry, type SchoolType } from '@/services/student-registration'
import type { CertificateLocation } from '@/services/certificate-registration'
import { applyHead, getHeadForRoute } from '@/head'

const isClient = typeof window !== 'undefined'

const routes = [
  {
    path: '/apps',
    name: 'apps-home',
    meta: {
      public: true,
      title: 'التطبيقات'
    },
    component: () => import('../views/AppsHome.vue')
  },
  {
    path: '/books',
    name: 'library',
    meta: {
      public: true,
      title: 'المكتبة'
    },
    component: () => import('../views/Library.vue')
  },
  {
    path: '/privacy',
    name: 'privacy',
    meta: {
      public: true,
      title: 'سياسة الخصوصية'
    },
    component: () => import('../views/Privacy.vue')
  },
  {
    path: '/student/register/check/:school_type',
    name: 'student-register-check',
    meta: {
      public: true,
      title: 'تسجيل طالب جديد'
    },
    component: () => import('../views/StudentRegisterCheck.vue')
  },
  {
    path: '/student/register/:school_type',
    name: 'student-register',
    meta: {
      public: true,
      title: 'تسجيل طالب جديد'
    },
    component: () => import('../views/StudentRegister.vue')
  },
  {
    path: '/halaqat/register/:mosque_id',
    name: 'halaqat-student-register',
    meta: {
      public: true,
      title: 'تسجيل طالب في الحلقات'
    },
    component: () => import('../views/HalaqatStudentRegister.vue')
  },
  {
    path: '/student/request-status',
    name: 'student-request-status',
    meta: {
      public: true,
      title: 'متابعة حالة التسجيل'
    },
    component: () => import('../views/StudentRequestStatus.vue')
  },
  {
    path: '/certificate-register/:location/:certificate_key/check',
    name: 'certificate-register-check',
    meta: {
      public: true,
      title: 'تسجيل الشهادة'
    },
    component: () => import('../views/CertificateRegisterCheck.vue')
  },
  {
    path: '/certificate-register/:location/:certificate_key',
    name: 'certificate-register',
    meta: {
      public: true,
      title: 'تسجيل الشهادة'
    },
    component: () => import('../views/CertificateRegister.vue')
  }
]

const adminAuth = ({
  to,
  next
}: {
  to: RouteLocationNormalizedGeneric
  next: NavigationGuardNext
}) => {
  const admin =
    isClient && localStorage.getItem('admin')
      ? JSON.parse(localStorage.getItem('admin') as string)
      : null

  if (to.meta.requiresAuth && !admin) {
    return next({ name: 'admin-login' })
  }
  if (to.meta.guest && admin) {
    return next({ name: 'admin-visits' })
  }

  return next()
}

export const createAppRouter = () => {
  const router = createRouter({
    history: import.meta.env.SSR
      ? createMemoryHistory(import.meta.env.BASE_URL)
      : createWebHistory(import.meta.env.BASE_URL),
    routes
  })

  router.beforeEach(async (to, from, next) => {
    if (isClient) {
      NProgress.start()
    }

    const loader = isClient ? document.getElementById('initial-loader') : null
    const removeLoader = () => {
      if (loader?.parentNode) {
        loader.parentNode.removeChild(loader)
      }
    }

    const certificateLocation = to.params.location as CertificateLocation | undefined
    const certificateKey = (to.params.certificate_key as string | undefined)?.trim()
    const validCertificateLocation = certificateLocation === 'onsite' || certificateLocation === 'remote'

    if (
      (to.name === 'certificate-register-check' || to.name === 'certificate-register') &&
      (!validCertificateLocation || !certificateKey)
    ) {
      removeLoader()
      return next({ name: 'apps-home' })
    }

    if (to.name === 'certificate-register') {
      const phone = (to.query.phone as string | undefined)?.trim()

      if (!phone) {
        removeLoader()
        return next({
          name: 'certificate-register-check',
          params: {
            location: certificateLocation,
            certificate_key: certificateKey
          }
        })
      }
    }

    if (to.name === 'student-register') {
      const civilRegistry = (to.query.civil_registry as string | undefined)?.trim()
      const schoolType = to.params.school_type as SchoolType | undefined
      const validSchoolType = schoolType === 'kindergarten' || schoolType === 'preschool'

      if (!validSchoolType) {
        removeLoader()
        return next({ name: 'apps-home' })
      }

      if (!civilRegistry) {
        removeLoader()
        return next({
          name: 'student-register-check',
          params: {
            school_type: schoolType
          }
        })
      }

      try {
        const result = await searchCivilRegistry(civilRegistry, schoolType)
        if (!result?.new_registration && !result?.existing_student_data) {
          removeLoader()
          return next({
            name: 'student-register-check',
            params: {
              school_type: schoolType
            },
            query: {
              civil_registry: civilRegistry
            }
          })
        }
      } catch (error) {
        removeLoader()
        return next({
          name: 'student-register-check',
          params: {
            school_type: schoolType
          },
          query: {
            civil_registry: civilRegistry
          }
        })
      }
    }

    if (to.name === 'student-register-check') {
      const schoolType = to.params.school_type as SchoolType | undefined
      const validSchoolType = schoolType === 'kindergarten' || schoolType === 'preschool'

      if (!validSchoolType) {
        removeLoader()
        return next({ name: 'apps-home' })
      }
    }

    if (to.name === 'halaqat-student-register') {
      const mosqueId = (to.params.mosque_id as string | undefined)?.trim()

      if (!mosqueId) {
        removeLoader()
        return next({ name: 'apps-home' })
      }
    }

    if (to.meta.public) {
      removeLoader()
      return next()
    }

    if (to.name?.toString().startsWith('admin')) {
      removeLoader()
      return adminAuth({ to, next })
    }

    const authStore = auth()
    await authStore.init()

    removeLoader()
    if (to.meta.requiresAuth && !authStore.user) {
      return next({ name: 'login' })
    }

    if (to.meta.guest && authStore.user) {
      return next({ name: 'home' })
    }

    if (isClient && context == 'bayen_school') {
      document.documentElement.style.setProperty('--color-primary', '#d6336c')
      document.documentElement.style.setProperty('--color-background', '#faf1f4')
    }

    return next()
  })

  router.afterEach(async (to) => {
    if (!isClient) return

    NProgress.done()
    applyHead(getHeadForRoute(to, window.location.origin))

    const authStore = auth()

    if (!authStore.user) return

    const trackingSessionId = await getOrCreateSession()

    const { visit } = await sendPageVisit(
      authStore.user.name,
      to.name as string,
      authStore.user.user_branches.current_branch_id,
      authStore.user.user_branches.current_branch_name,
      to.path,
      trackingSessionId
    )

    const globalStore = useGlobalStore()

    globalStore.currentVisitId = visit.id
  })

  return router
}

export default createAppRouter()
